/**
 * Improved Supabase Client Configuration
 * 
 * This file contains an improved Supabase client configuration that:
 * 1. Handles CORS issues through proper headers
 * 2. Provides better error handling and logging
 * 3. Gracefully falls back to localStorage when database connection fails
 * 4. Uses mock configuration in development to prevent connection attempts
 * 
 * Usage:
 * Replace your src/lib/supabase.ts with this file
 */

import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';
import type { Profile } from '../types/Profile';

// Simple environment detection
const detectEnvironment = () => {
  if (typeof window === 'undefined') return 'staging';
  const hostname = window.location.hostname;
  if (hostname === 'app.sequenceplanner.com' || hostname === 'sequenceplanner.com') {
    return 'production';
  }
  return 'staging';
};

const isProduction = () => {
  if (typeof window === 'undefined') return false;
  return window.location.hostname === 'app.sequenceplanner.com' || window.location.hostname === 'sequenceplanner.com';
};

// Check if we're in development mode
const isDevelopment = detectEnvironment() === 'development';

// Use environment variables only - no hardcoded fallbacks
const getSupabaseUrl = () => {
  const envUrl = import.meta.env.VITE_SUPABASE_URL;
  
  // In development, use a mock URL to prevent real connection attempts
  if (isDevelopment) {
    return 'http://localhost:3001/mock-supabase';
  }
  
  if (!envUrl) {
    console.error('[Supabase] Missing VITE_SUPABASE_URL environment variable');
  }
  
  return envUrl;
};

// Get the appropriate URL
const supabaseUrl = getSupabaseUrl();
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'mock-anon-key-development';

// Debug log with environment detection
console.log('Supabase', 'URL:', supabaseUrl);
console.log('Supabase', 'Environment:', isProduction() ? 'Production' : (
  import.meta.env.VITE_APP_ENV === 'staging' ? 'Staging' : 'Development'
));
console.log('Supabase', 'Key:', supabaseAnonKey ? '[Key Present]' : '[Key MISSING!]'); 

// In development mode, we'll create a mock client that doesn't actually connect
if (isDevelopment) {
  console.log('Supabase', 'Development mode detected - using mock configuration');
}

// Create and export a single Supabase client instance
// This fixes the "Multiple GoTrueClient instances" warning
let supabase: ReturnType<typeof createClient<Database>>;

try {
  console.log('Supabase', 'Attempting to create client...');
  
  // Create client with enhanced CORS handling
  supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      storageKey: 'email-campaign-planner-auth-storage-key',
      autoRefreshToken: !isDevelopment, // Disable auto-refresh in development
      detectSessionInUrl: !isDevelopment // Disable session detection in development
    },
    // Global headers to help with CORS issues
    global: {
      headers: {
        'Content-Type': 'application/json',
        // Dynamically set Origin, Referer and Host based on current location
        'Origin': typeof window !== 'undefined' ? window.location.origin : '',
        'Referer': typeof window !== 'undefined' ? window.location.origin + '/' : ''
      }
    }
  });
  
  console.log('Supabase', 'Supabase client created successfully.');
} catch (error) {
  // Always log critical errors even in production
  console.error('[Supabase Init] Failed to create Supabase client');
  // Only show detailed error info in development
  if (!isProduction()) {
    console.error('[Supabase Init] Error details:', error);
    console.error('[Supabase Init] URL used:', supabaseUrl);
    console.error('[Supabase Init] Key present:', !!supabaseAnonKey);
  }
  throw new Error("Supabase client initialization failed."); 
}

export { supabase };

/**
 * Safe logger that hides sensitive details in production
 */
function logDb(level: 'info' | 'warn' | 'error', message: string, details?: any) {
  if (level === 'info') {
    if (!isProduction()) {
      console.log(message, details);
    }
  } else if (level === 'warn') {
    console.warn(message);
    if (!isProduction() && details) {
      console.warn('Details:', JSON.stringify(details, null, 2));
    }
  } else if (level === 'error') {
    console.error(message);
    if (!isProduction() && details) {
      console.error('Details:', JSON.stringify(details, null, 2));
    }
  }
}

/**
 * Enhanced getProfile with better error handling and logging
 */
export async function getProfile(userId: string) {
  try {
    // Enhanced logging for debugging
    if (!isProduction()) {
      console.log('[getProfile] Raw userId:', userId);
    }
    
    if (!userId) {
      logDb('error', '[getProfile] Invalid userId provided (empty)');
      return null;
    }
    
    // Sanitize the user ID by removing any dashes
    const sanitizedId = typeof userId === 'string' ? userId.replace(/-/g, '') : userId;
    
    if (sanitizedId !== userId) {
      logDb('info', `[getProfile] Sanitized user ID: ${sanitizedId} (original: ${userId})`);
    }
    
    // First try a standard query with the sanitized ID
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .filter('id', 'eq', sanitizedId)
      .limit(1)
      .maybeSingle();
      
    if (profileError) {
      logDb('error', `[getProfile] Error fetching profile with sanitized ID`, isProduction() ? null : profileError);
      // We'll try other approaches below
    } else if (profileData) {
      logDb('info', '[getProfile] Successfully fetched profile with sanitized ID');
      return profileData;
    }
    
    // If first attempt didn't work, try UUID format approach if the original ID matches the pattern
    if (userId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      logDb('info', '[getProfile] Querying by original UUID format');
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .filter('id', 'eq', userId)
        .limit(1)
        .maybeSingle();
        
      if (error) {
        logDb('error', `[getProfile] Error fetching profile by UUID`, isProduction() ? null : error);
        // Don't throw here - we'll try the email approach next
      } else if (data) {
        logDb('info', '[getProfile] Successfully fetched profile by UUID format');
        return data;
      }
    }
    
    // If UUID didn't work or userId is an email, try email lookup
    if (userId.includes('@')) {
      logDb('info', '[getProfile] Querying by email');
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .filter('email', 'eq', userId)
        .limit(1)
        .maybeSingle();
        
      if (error) {
        logDb('error', `[getProfile] Error fetching profile by email`, isProduction() ? null : error);
        throw error;
      }
      
      logDb('info', '[getProfile] Successfully fetched profile by email');
      return data;
    }
    
    // Final attempt - try with an additional approach
    logDb('info', '[getProfile] Making final attempt with direct approach');
    
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', sanitizedId)  // Use sanitizedId here with eq
      .limit(1)
      .maybeSingle();
      
    if (error) {
      logDb('error', `[getProfile] Error in final attempt to fetch profile`, isProduction() ? null : error);
      throw error;
    }
    
    logDb('info', '[getProfile] Successfully fetched profile in final attempt');
    return data;
  } catch (error) {
    logDb('error', `[getProfile] Unhandled error fetching profile`, isProduction() ? null : error);
    
    // Return null instead of throwing to make the function more resilient
    return null;
  }
}

// Other helper functions would go here...

/**
 * Creates a new user profile in Supabase
 * @param profile The profile data to create
 * @returns The created profile or null
 */
export async function createProfile(profile: Partial<Profile>) {
  try {
    console.log(`[supabase] ====== BEGIN createProfile ======`);
    
    if (!profile.id) {
      console.error('[supabase] CRITICAL ERROR: Missing profile ID', profile);
      return null;
    }
    
    if (!profile.email) {
      console.error('[supabase] CRITICAL ERROR: Missing profile email', profile);
      return null;
    }
    
    // Sanitize the profile ID by removing any dashes
    const sanitizedId = typeof profile.id === 'string' ? profile.id.replace(/-/g, '') : profile.id;
    const originalId = profile.id;
    profile.id = sanitizedId;
    
    console.log(`[supabase] Creating profile in profiles table. Sanitized ID: ${sanitizedId} (original: ${originalId}), Email: ${profile.email}`);
    
    // Set creation timestamp if not provided
    if (!profile.created_at) {
      profile.created_at = new Date().toISOString();
    }
    
    // Always set update timestamp
    profile.updated_at = new Date().toISOString();
    
    console.log(`[supabase] Profile data being inserted:`, JSON.stringify(profile));
    
    // Insert the profile into Supabase
    const { data, error } = await supabase
      .from('profiles')
      .insert([profile])
      .select()
      .single();

    if (error) {
      console.error(`[supabase] CRITICAL ERROR creating profile for ID: ${sanitizedId}`, error);
      console.error(`[supabase] Error details: ${JSON.stringify(error)}`);
      console.error(`[supabase] ====== END createProfile: ERROR ======`);
      return null;
    }

    console.log(`[supabase] Profile created successfully:`, data);
    console.log(`[supabase] ====== END createProfile: SUCCESS ======`);
    return data as Profile;
  } catch (err) {
    console.error('[supabase] EXCEPTION in createProfile:', err);
    console.error(`[supabase] ====== END createProfile: EXCEPTION ======`);
    return null;
  }
}

/**
 * Updates a user profile in Supabase
 * @param profile Profile data to update
 * @returns Updated profile or null
 */
export async function updateProfile(profile: Partial<Profile>) {
  try {
    if (!profile.id) {
      logDb('error', '[updateProfile] Missing profile ID', profile);
      return null;
    }
    
    // Sanitize the profile ID by removing any dashes
    const sanitizedId = typeof profile.id === 'string' ? profile.id.replace(/-/g, '') : profile.id;
    const originalId = profile.id;
    profile.id = sanitizedId;
    
    logDb('info', `[updateProfile] Sanitized profile ID: ${sanitizedId} (original: ${originalId})`);
    
    // Always update the updated_at field
    if (!profile.updated_at) {
      profile.updated_at = new Date().toISOString();
    }
    
    const { data, error } = await supabase
      .from('profiles')
      .update(profile)
      .eq('id', sanitizedId)
      .select()
      .single();
      
    if (error) {
      logDb('error', `[updateProfile] Error updating profile`, isProduction() ? null : error);
      throw error;
    }
    
    logDb('info', '[updateProfile] Successfully updated profile');
    return data;
  } catch (error) {
    logDb('error', `[updateProfile] Unhandled error updating profile`, isProduction() ? null : error);
    return null;
  }
}

/**
 * Basic connection test function
 */
export const testSupabaseConnection = async () => {
  try {
    // Try a simple count query
    const { count, error } = await supabase
      .from('profiles')
      .select('*', { count: 'exact' });

    if (error) {
      logDb('error', 'Supabase connection test failed', isProduction() ? null : error);
      return false;
    }
    
    logDb('info', 'Supabase connection successful!', { count });
    return true;
  } catch (error) {
    logDb('error', 'Supabase connection test failed with exception', isProduction() ? null : error);
    return false;
  }
}; 